import React, { useEffect, useState, useCallback } from "react";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useLocation } from "react-router-dom";
import { getPermissions } from "../rbac/hasAccess";
import { formateDate, paginationrowsPerPageOptions } from "../../../utils/commonComponent/commonConstant";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { AutoCompletes, DateInput } from "../../../utils/Custom/custominput";
import ExcelDownload from "../../../utils/commonComponent/ExcelDownload";
import { SearchToggleButton } from "../../../utils/Custom/Button";

const headers = [
  "id",
  "enquiryNo",
  "enquiryDate",
  "customerName",
  "mode_of_enquiry",
  "customer_contact_number",
  "customer_email_id",
  "services_offer",
  "enquiry_item",
  "domain",
  "dyc_client_code",
  "service_requirement",
  "enquiryReviewDoneBy",
  "reviewedByTMBD1",
  "quotationNumber",
  "quotationDate",
  "reviewedByTMBD2",
  "quotationSubmittedOn",
  "followUpStatus",
  "poNumber",
  "poDate",
  "poAmount",
  "dycControlNo",
  "remark",
];

const notNeededColumn = ["id"];

const toSqlDate = (val) => {
  if (!val) return null;
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0];
};

const EnquiryRegisterReport = (props) => {
  const location = useLocation();
  const permissions = getPermissions(props.moduleId);

  const [reportList, setReportList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  const [from, setFromDate] = useState(null);
  const [to, setToDate] = useState(null);
  const [searched, setSearched] = React.useState(false);

  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const getWhereClause = () => {
    let whereConditions = "";

    const fromSql = toSqlDate(from);
    const toSql = toSqlDate(to);
    if (fromSql != null && toSql != null) {
      whereConditions += whereConditions ? " AND" : " WHERE";
      whereConditions += ` DATE(e.enquiryDate) BETWEEN '${fromSql}' AND '${toSql}'`;
    }

    return whereConditions;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause();
    let paginationSettings = "";
    if (rowsPerPage !== -1) {
      paginationSettings = `LIMIT ${rowsPerPage} OFFSET ${rowsPerPage * Number(currentPage)}`;
    }
    return `SELECT
        e.id AS id,
        e.enquiryNo,
        e.enquiryDate,
        e.customerName,
        e.mode_of_enquiry,
        e.customer_contact_number,
        e.customer_email_id,
        e.services_offer,
        e.enquiry_item,
        e.domain,
        e.dyc_client_code,
        e.service_requirement,
        emp1.name AS enquiryReviewDoneBy,
        emp2.name AS reviewedByTMBD1,
        qt.quotationNumber,
        qt.date AS quotationDate,
        emp2.name AS reviewedByTMBD2,
        qt.lastModified AS quotationSubmittedOn,
        e.followupRemark AS followUpStatus,
        po.poNumber,
        po.poDate,
        po.amount AS poAmount,
        po.id AS dycControlNo,
        e.followupRemark AS remark
        FROM enquiry e
        LEFT JOIN quotation qt ON qt.enquiryId = e.id
        LEFT JOIN poAcceptance po ON po.quotationId = qt.id
        LEFT JOIN users u1 ON e.enquiry_review = u1.id
        LEFT JOIN employees emp1 ON u1.employeeId = emp1.id
        LEFT JOIN users u2 ON e.reviewedBy = u2.id
        LEFT JOIN employees emp2 ON u2.employeeId = emp2.id
        ${whereClause}
        ORDER BY e.id DESC
        ${paginationSettings};`
      .trim()
      .replace(/\s+/g, " ");
  };

  const getCountQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT COUNT(e.id) AS totalRows FROM enquiry e LEFT JOIN quotation qt ON qt.enquiryId = e.id LEFT JOIN poAcceptance po ON po.quotationId = qt.id ${whereClause};`
      .trim()?.replace(/\s+/g, " ");
  };

  const fetchReportData = async () => {
    try {
      const [dataResult, totalRowsResult] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
      ]);
      setReportList(dataResult || []);
      setTotalRows(totalRowsResult?.[0]?.totalRows || 0);
    } catch (error) {
      console.error("Error fetching enquiry master report:", error);
    }
  };

  const handleSearch = () => {
    setSearched(true);
    setCurrentPage(0);
    fetchReportData();
  };

  const clearSearch = () => {
    setSearched(false);
    setReportList([]);
    setFromDate(null);
    setToDate(null);
  };

  useEffect(() => {
    if (searched) {
      fetchReportData();
    }
  }, [currentPage, rowsPerPage]);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2 my-3">
        <div className="w-full">
          <DateInput
            id="from-date"
            label="From Date"
            value={from}
            onChange={(val) => {
              setFromDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div className="w-full">
          <DateInput
            id="to-date"
            label="To Date"
            value={to}
            onChange={(val) => {
              setToDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div>
          <SearchToggleButton
            searched={searched}
            onSearch={handleSearch}
            onClear={clearSearch}
          />
        </div>
        {reportList?.length > 0 &&
          (permissions.excelDownload || permissions.allAccess) && (
            <div>
              <ExcelDownload
                finalData={reportList}
                notNeededColumn={notNeededColumn}
              />
            </div>
          )}
      </div>

      <div className="w-full border rounded-md shadow">
        <div className="overflow-y-auto h-auto max-h-[550px]">
          <table className="min-w-full text-sm text-left text-gray-700">
            <thead className="sticky top-0 bg-blue-50 text-xs font-medium text-gray-600 z-10">
              <tr>
                <th className="px-4 py-3 whitespace-nowrap">Sr. No.</th>
                <th className="px-4 py-3 whitespace-nowrap">Enquiry No.</th>
                <th className="px-4 py-3 whitespace-nowrap">Enquiry Received Date</th>
                <th className="px-4 py-3 whitespace-nowrap">Customer Name</th>
                <th className="px-4 py-3 whitespace-nowrap">Mode of Enquiry</th>
                <th className="px-4 py-3 whitespace-nowrap">Customer Contact Number</th>
                <th className="px-4 py-3 whitespace-nowrap">Customer E-mail Id</th>
                <th className="px-4 py-3 whitespace-nowrap">Services Offer</th>
                <th className="px-4 py-3 whitespace-nowrap">Enquiry for Item</th>
                <th className="px-4 py-3 whitespace-nowrap">Domain</th>
                <th className="px-4 py-3 whitespace-nowrap">DYC Client Code</th>
                <th className="px-4 py-3 whitespace-nowrap">Service Requirement (Mandays/Monthly/Lump Sum)</th>
                <th className="px-4 py-3 whitespace-nowrap">Enquiry Review Done as per DYC/FMT-MR/44</th>
                <th className="px-4 py-3 whitespace-nowrap">Reviewed By Technical Manager & BD Manager</th>
                <th className="px-4 py-3 whitespace-nowrap">Quotation No.</th>
                <th className="px-4 py-3 whitespace-nowrap">Quotation Date</th>
                <th className="px-4 py-3 whitespace-nowrap">Reviewed By Technical Manager & BD Manager</th>
                <th className="px-4 py-3 whitespace-nowrap">Quotation Submitted On</th>
                <th className="px-4 py-3 whitespace-nowrap">Follow Up Status</th>
                <th className="px-4 py-3 whitespace-nowrap">Client Purchase Order No.</th>
                <th className="px-4 py-3 whitespace-nowrap">Client Purchase Order Date</th>
                <th className="px-4 py-3 whitespace-nowrap">P.O. Amount</th>
                <th className="px-4 py-3 whitespace-nowrap">DYC Control No.</th>
                <th className="px-4 py-3 whitespace-nowrap">Remark</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reportList?.length > 0 ? (
                reportList.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="px-4 py-3">{currentPage * rowsPerPage + rowIndex + 1}</td>
                    <td className="px-4 py-3">{row.enquiryNo ?? "-"}</td>
                    <td className="px-4 py-3">{formateDate(row.enquiryDate)}</td>
                    <td className="px-4 py-3">{row.customerName ?? "-"}</td>
                    <td className="px-4 py-3">{row.mode_of_enquiry ?? "-"}</td>
                    <td className="px-4 py-3">{row.customer_contact_number ?? "-"}</td>
                    <td className="px-4 py-3">{row.customer_email_id ?? "-"}</td>
                    <td className="px-4 py-3">{row.services_offer ?? "-"}</td>
                    <td className="px-4 py-3">{row.enquiry_item ?? "-"}</td>
                    <td className="px-4 py-3">{row.domain ?? "-"}</td>
                    <td className="px-4 py-3">{row.dyc_client_code ?? "-"}</td>
                    <td className="px-4 py-3">{row.service_requirement ?? "-"}</td>
                    <td className="px-4 py-3">{row.enquiryReviewDoneBy ?? "-"}</td>
                    <td className="px-4 py-3">{row.reviewedByTMBD1 ?? "-"}</td>
                    <td className="px-4 py-3">{row.quotationId == 0 ? "Verbal" : row.quotationNumber ?? "-"}</td>
                    <td className="px-4 py-3">{formateDate(row.quotationDate)}</td>
                    <td className="px-4 py-3">{row.reviewedByTMBD2 ?? "-"}</td>
                    <td className="px-4 py-3">{formateDate(row.quotationSubmittedOn)}</td>
                    <td className="px-4 py-3">{row.followUpStatus ?? "-"}</td>
                    <td className="px-4 py-3">{row.poNumber ?? "-"}</td>
                    <td className="px-4 py-3">{formateDate(row.poDate)}</td>
                    <td className="px-4 py-3">{row.poAmount ?? "-"}</td>
                    <td className="px-4 py-3">{row.dycControlNo ?? "-"}</td>
                    <td className="px-4 py-3">{row.remark ?? "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={headers.length} className="text-center py-4 text-gray-500">
                    No data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-2 bg-blue-50 text-sm border-t">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) =>
                handlePaginationChange(0, Number(e.target.value))
              }
              className="border rounded px-2 py-1 text-sm"
            >
              {paginationrowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-600">
              {totalRows === 0
                ? "0–0 of 0"
                : `${currentPage * rowsPerPage + 1}–${Math.min(
                    (currentPage + 1) * rowsPerPage,
                    totalRows
                  )} of ${totalRows}`}
            </span>
            <button
              onClick={() => handlePaginationChange(0, rowsPerPage)}
              disabled={currentPage === 0}
              className="p-1 disabled:opacity-50"
            >
              <FirstPageIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(currentPage - 1, rowsPerPage)
              }
              disabled={currentPage === 0}
              className="p-1 disabled:opacity-50"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(currentPage + 1, rowsPerPage)
              }
              disabled={currentPage >= Math.ceil(totalRows / rowsPerPage) - 1}
              className="p-1 disabled:opacity-50"
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(
                  Math.ceil(totalRows / rowsPerPage) - 1,
                  rowsPerPage
                )
              }
              disabled={currentPage >= Math.ceil(totalRows / rowsPerPage) - 1}
              className="p-1 disabled:opacity-50"
            >
              <LastPageIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryRegisterReport;